import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import SectionMessage from '@atlaskit/section-message';
import { type Extension } from '../../web-client';
import { createAtlAttributionHeader } from '../../utils';
// eslint-disable-next-line import/extensions
import { type ForgeDeprecationMessage_appQuery as appQuery } from './__generated__/ForgeDeprecationMessage_appQuery.graphql';
import { RelaySuspensefulBoundary } from '../../utils/RelaySuspensefulBoundary';

export const SANDBOX_DEPRECATION_ERROR = 'INVALID_INVOCATION_RUNTIME_ERROR';

interface ForgeDeprecationMessageProps {
	error: string;
	extension: Extension;
}

const buildMessage = (title: string, error?: string) => {
	if (error === SANDBOX_DEPRECATION_ERROR) {
		return `Your app ${title} is no longer working as it uses a deprecated platform feature. Please check if an update for this app is available through Atlassian Administration, or ask the app developer to upgrade the app.\nIf the app is listed on the marketplace, contact the developer using the Support tab.`;
	}

	return `${title} version is outdated due to a deprecated platform component.\nThe app developer needs to update it to a compatible version.\nPlease ask your admin to check for updates or contact the app developer.`;
};

const ForgeDeprecationMessageInner = (props: ForgeDeprecationMessageProps): React.JSX.Element => {
	const { extension, error } = props;

	const data = useLazyLoadQuery<appQuery>(
		graphql`
			query ForgeDeprecationMessage_appQuery($id: ID!) {
				app(id: $id) {
					name
				}
			}
		`,
		{
			id: extension?.id,
		},
		{
			networkCacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
					},
				},
			},
		},
	);

	const title = data.app?.name ? data.app?.name : extension.properties.title;
	const message = buildMessage(title, error);

	return (
		<SectionMessage title="App update required" appearance="error">
			{message}
		</SectionMessage>
	);
};

const ForgeDeprecationMessageFallback = (
	props: ForgeDeprecationMessageProps,
): React.JSX.Element => {
	const { extension, error } = props;
	const title = extension.properties.title;
	const message = buildMessage(title, error);
	return (
		<SectionMessage title="App update required" appearance="error">
			{message}
		</SectionMessage>
	);
};

export const ForgeDeprecationMessage = (props: ForgeDeprecationMessageProps): React.JSX.Element => {
	const fallback = <ForgeDeprecationMessageFallback {...props} />;
	return (
		<RelaySuspensefulBoundary fallback={fallback} errorFallback={fallback}>
			<ForgeDeprecationMessageInner {...props} />
		</RelaySuspensefulBoundary>
	);
};
